import React, { useContext, useEffect, useRef } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import PropTypes from 'prop-types';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import EditProductView from '../products/EditProductView';
import EditProductVariantsView from '../products/EditProductVariantsView';
import { useAdminBackPath } from '../../../helpers/adminHelper';

const AdminProductDetailsView = ({ newProduct }) => {
  const navigate = useNavigate();
  const fetchedPkEcomExternalProduct = useRef(null);
  const { pathname } = useLocation();
  const { pkEcomExternalProduct } = useParams();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchEcomExternalProduct,
    updateEcomExternalProducts,
    deleteEcomExternalProducts,
    syncEcomExternalProductsWithProviders
  } = useContext(AdminContext);

  const { backPath } = useAdminBackPath(
    pkEcomExternalProduct && pathname.includes('/duplicate')
      ? `/admin/products/${pkEcomExternalProduct}`
      : `/admin/products/`
  );

  const ecomExternalProduct =
    state.ecomExternalProduct.value?.pkEcomVendorProduct.toString() ===
    pkEcomExternalProduct
      ? state.ecomExternalProduct.value
      : null;

  const isLoading =
    (pkEcomExternalProduct && !fetchedPkEcomExternalProduct.current) ||
    state.ecomExternalProduct.loading;

  const imageUploadBaseUrl = `ecom-admin-external-product-${authState.userData.user.pkUser}`;

  useEffect(() => {
    if (
      pkEcomExternalProduct &&
      fetchedPkEcomExternalProduct.current !== pkEcomExternalProduct &&
      !newProduct
    ) {
      // eslint-disable-next-line no-use-before-define
      fetchEcomExternalProduct(pkEcomExternalProduct, null, (error) => {
        triggerNotification('Product not found!');
      });
      fetchedPkEcomExternalProduct.current = pkEcomExternalProduct;
    }
  }, [pkEcomExternalProduct]);

  const onCreateProduct = (requestData, onSuccessCallback, onErrorCallback) => {
    updateEcomExternalProducts(
      {
        ecomExternalProducts: [
          {
            ...requestData,
            createNew: true
          }
        ],
        selectFirstItem: true
      },
      (data) => {
        navigate(`/admin/products/${data[0]?.pkEcomExternalProduct}`);
        fetchedPkEcomExternalProduct.current = data[0]?.pkEcomExternalProduct;
        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      onErrorCallback
    );
  };

  const onUpdateProduct = (requestData, onSuccessCallback, onErrorCallback) => {
    updateEcomExternalProducts(
      { ecomExternalProducts: [requestData], selectFirstItem: true },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const onDeleteProduct = (productId, onSuccessCallback, onErrorCallback) => {
    deleteEcomExternalProducts(
      [productId],
      (data) => {
        navigate('/admin/products');
        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      onErrorCallback
    );
  };

  const onSyncExternalProduct = (
    requestData,
    onSuccessCallback,
    onErrorCallback
  ) => {
    syncEcomExternalProductsWithProviders(
      requestData,
      (data) => {
        fetchEcomExternalProduct(pkEcomExternalProduct);
        onSuccessCallback(data);
      },
      onErrorCallback
    );
  };

  const productDetailsProps = {
    ecomVendorProduct: ecomExternalProduct,
    onCreateProduct,
    onUpdateProduct,
    onDeleteProduct,
    onSyncExternalProduct,
    productId: pkEcomExternalProduct,
    isLoading,
    baseUrl: `/admin/products`,
    imageUploadBaseUrl,
    backPath,
    isAdminExternalProductView: true,
    productUuid: pkEcomExternalProduct
  };

  return (
    <Routes>
      <Route
        element={
          <EditProductVariantsView {...productDetailsProps} newVariant />
        }
        path="/variants/new"
      />
      <Route
        element={<EditProductVariantsView {...productDetailsProps} />}
        path="/variants/:variantUuid"
      />
      <Route
        element={<EditProductVariantsView {...productDetailsProps} duplicate />}
        path="/variants/:variantUuid/duplicate"
      />
      <Route
        element={<EditProductVariantsView {...productDetailsProps} />}
        path="/variants"
      />
      <Route
        element={<EditProductView {...productDetailsProps} duplicate />}
        path="/duplicate"
      />
      <Route element={<EditProductView {...productDetailsProps} />} path="/" />
      <Route element={<Navigate replace to="/admin/products*" />} path="*" />
    </Routes>
  );
};

AdminProductDetailsView.propTypes = { newProduct: PropTypes.bool };

export default AdminProductDetailsView;
